import { FormEvent, useEffect, useState } from 'react'
import { Plus, Building2, CheckCircle, CreditCard, ArrowUpRight, DollarSign } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createSubscriber, adminAdjustCredits, fetchReadCreditConfig } from '../api/client'
import { useOrgContext } from '../state/OrgContext'

const formatNumber = (n: number) => n.toLocaleString()

export default function SubscribersPage() {
  const { t } = useTranslation('pages')
  const { subscribers, activeSubId, reload, setActiveSub, loading, activeRole } = useOrgContext()
  const [submitting, setSubmitting] = useState(false)
  const [switchingId, setSwitchingId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [subscriberType, setSubscriberType] = useState<'INDIVIDUAL' | 'INSTITUTION'>('INSTITUTION')

  /* Credit adjustment state — per subscriber */
  const [creditInputs, setCreditInputs] = useState<Record<number, string>>({})
  const [creditStatus, setCreditStatus] = useState<Record<number, { saving?: boolean; message?: string; isError?: boolean }>>({})
  const [readsPerUsd, setReadsPerUsd] = useState<number>(7)

  useEffect(() => {
    fetchReadCreditConfig().then((cfg) => setReadsPerUsd(cfg.reads_per_usd)).catch(() => {})
  }, [])

  const onCreateSubscriber = async (event: FormEvent) => {
    event.preventDefault()
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError(t('subscribers.nameRequired'))
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await createSubscriber({ name: trimmedName, subscriber_type: subscriberType })
      setName('')
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscribers.createError'))
    } finally {
      setSubmitting(false)
    }
  }

  const onSetActive = async (subId: number) => {
    setSwitchingId(subId)
    setError(null)
    try {
      await setActiveSub(subId)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscribers.activateError'))
    } finally {
      setSwitchingId(null)
    }
  }

  const onAdjustCredits = async (subId: number) => {
    const raw = creditInputs[subId] || ''
    const amtUsd = Number(raw)
    if (!Number.isFinite(amtUsd) || amtUsd === 0) {
      setCreditStatus((s) => ({ ...s, [subId]: { message: t('subscribers.invalidAmount'), isError: true } }))
      return
    }
    setCreditStatus((s) => ({ ...s, [subId]: { saving: true } }))
    try {
      const resp = await adminAdjustCredits(subId, amtUsd)
      setCreditInputs((s) => ({ ...s, [subId]: '' }))
      setCreditStatus((s) => ({
        ...s,
        [subId]: {
          message: t('subscribers.topUpSuccess', {
            added: resp.reads_added.toFixed(1),
            balance: resp.read_balance.toFixed(1),
          }),
          isError: false,
        },
      }))
      await reload()
    } catch (err) {
      setCreditStatus((s) => ({
        ...s,
        [subId]: { message: err instanceof Error ? err.message : t('subscribers.topUpError'), isError: true },
      }))
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('subscribers.title')}</h2>
        <p className="page-header-sub">{t('subscribers.subtitle')}</p>
      </div>

      {error && (
        <div className="card card-padding mb-20" style={{ borderLeft: '3px solid var(--color-danger)' }}>
          <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>
        </div>
      )}

      <div style={{ maxWidth: 860 }}>
        {/* Create subscriber form — admin only */}
        {activeRole === 'ADMIN' && (
          <div className="card card-padding mb-24">
            <h3 className="card-title mb-16">{t('subscribers.createCard')}</h3>
            <form className="flex gap-12" onSubmit={onCreateSubscriber}>
              <div className="form-group" style={{ flex: 1 }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t('subscribers.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ width: 180 }}>
                <select
                  className="form-select"
                  value={subscriberType}
                  onChange={(e) => setSubscriberType(e.target.value as 'INDIVIDUAL' | 'INSTITUTION')}
                >
                  <option value="INSTITUTION">{t('subscribers.typeInstitution')}</option>
                  <option value="INDIVIDUAL">{t('subscribers.typeIndividual')}</option>
                </select>
              </div>
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                <Plus size={16} />
                {submitting ? t('common.creating') : t('common.create')}
              </button>
            </form>
          </div>
        )}

        {/* Subscriber list */}
        <div className="card">
          {loading ? (
            <div className="card-padding">
              <p className="text-sm text-muted">{t('subscribers.subscribersLoading')}</p>
            </div>
          ) : subscribers.length === 0 ? (
            <div className="card-padding">
              <p className="text-sm text-muted">{t('subscribers.subscribersEmpty')}</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('subscribers.colSubscriber')}</th>
                    <th>{t('subscribers.colType')}</th>
                    {activeRole === 'ADMIN' && <th style={{ textAlign: 'right' }}>{t('subscribers.colBalance')}</th>}
                    <th>{t('subscribers.colStatus')}</th>
                    <th></th>
                    {activeRole === 'ADMIN' && <th>{t('subscribers.colManage')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub) => {
                    const isActive = activeSubId === sub.id
                    const status = creditStatus[sub.id]
                    return (
                      <tr key={sub.id}>
                        <td>
                          <div className="flex items-center gap-8">
                            <Building2 size={16} style={{ color: 'var(--text-muted)' }} />
                            <span className="font-semibold">{sub.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-neutral">
                            {sub.subscriber_type === 'INSTITUTION'
                              ? t('subscribers.badgeInstitution')
                              : t('subscribers.badgeIndividual')}
                          </span>
                        </td>
                        {activeRole === 'ADMIN' && (
                          <td style={{ textAlign: 'right' }}>
                            <div className="flex items-center gap-4" style={{ justifyContent: 'flex-end' }}>
                              <CreditCard size={14} style={{ color: 'var(--accent-copper)' }} />
                              <span className="font-heading font-semibold">
                                {sub.read_balance != null ? sub.read_balance.toFixed(1) : '-'}
                              </span>
                              <span className="text-muted text-sm">{t('subscribers.readingsUnit')}</span>
                            </div>
                          </td>
                        )}
                        <td>
                          {isActive ? (
                            <span className="badge badge-success">
                              <CheckCircle size={12} />
                              {t('subscribers.activeBadge')}
                            </span>
                          ) : (
                            <span className="text-muted text-sm">{t('subscribers.inactiveText')}</span>
                          )}
                        </td>
                        <td>
                          {!isActive && (
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => onSetActive(sub.id)}
                              disabled={switchingId === sub.id}
                            >
                              {switchingId === sub.id ? t('subscribers.activating') : t('subscribers.activate')}
                            </button>
                          )}
                        </td>
                        {activeRole === 'ADMIN' && (
                          <td>
                            <div className="flex gap-8 items-center">
                              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <DollarSign size={12} style={{ position: 'absolute', left: 6, color: 'var(--text-muted)', pointerEvents: 'none' }} />
                                <input
                                  type="number"
                                  className="form-input"
                                  style={{ width: 90, padding: '4px 8px 4px 20px', fontSize: '0.8rem' }}
                                  placeholder="USD"
                                  min="0"
                                  step="1"
                                  value={creditInputs[sub.id] || ''}
                                  onChange={(e) =>
                                    setCreditInputs((s) => ({ ...s, [sub.id]: e.target.value }))
                                  }
                                />
                              </div>
                              {creditInputs[sub.id] && Number(creditInputs[sub.id]) > 0 && (
                                <span className="text-xs text-muted" style={{ whiteSpace: 'nowrap' }}>
                                  {t('subscribers.topUpPreview', { value: (Number(creditInputs[sub.id]) * readsPerUsd).toFixed(1) })}
                                </span>
                              )}
                              <button
                                className="btn btn-copper btn-sm"
                                style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                onClick={() => onAdjustCredits(sub.id)}
                                disabled={status?.saving}
                              >
                                <ArrowUpRight size={12} />
                                {status?.saving ? t('subscribers.topUpLoading') : t('subscribers.topUp')}
                              </button>
                              {status?.message && (
                                <span
                                  className="text-sm"
                                  style={{
                                    color: status.isError ? 'var(--color-danger)' : 'var(--color-success)',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {status.message}
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
